import { endSession, firstInTab, SIGN_IN_HINT } from "@khazzin/auth";
import { Forklift } from "@khazzin/ui/illustrations/Forklift";
import { Button } from "@khazzin/ui/primitives/Button";
import { useAuth } from "@workos-inc/authkit-react";
import { type ReactNode, useEffect, useState } from "react";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { type PendingWork, useOffline } from "../../offline/Offline";

/**
 * The signed-out screen. A driver who has signed in on this phone and lost
 * signal never sees it: the app opens on the cached day instead (EP10-S15).
 *
 * ADJ-M03-4: when the seven-day rule has just deleted work that never
 * reached the server, it says so, so the driver can tell dispatch.
 */
export function Home() {
	const { isLoading, signIn } = useAuth();
	const { t, locale, setLocale } = useI18n();
	const { lost, online } = useOffline();
	// ADJ-1022-4: sent here signed in elsewhere (the console or the portal adds
	// the hint), or back on `/callback` with a code AuthKit refused — either
	// way WorkOS holds a session, so the sign-in starts on its own rather than
	// asking for another tap. Once per tab, so a failing one stops at the button.
	useEffect(() => {
		if (isLoading || !online || !autoSignIn(window.location)) {
			return;
		}
		if (firstInTab(AUTO_SIGN_IN_KEY)) {
			void signIn();
		}
	}, [isLoading, online, signIn]);

	return (
		<Card>
			<div className="flex flex-col gap-1">
				<h1 className="text-[15px] font-bold tracking-tight text-fg">{t("driver.home.title")}</h1>
				<p className="text-[13px] leading-relaxed text-fg-muted">{t("driver.home.signedOut")}</p>
			</div>
			{lost !== null && (
				<p
					role="alert"
					className="rounded-md bg-warning-bg p-3 text-start text-[13px] text-warning-fg"
				>
					{t("driver.home.lost", { commands: lost.commands, files: lost.files })}
				</p>
			)}
			<Button variant="primary" size="lg" disabled={isLoading} onClick={() => void signIn()}>
				{t("driver.home.signIn")}
			</Button>
			<Button
				variant="ghost"
				size="sm"
				lang={locale === "ar" ? "en" : "ar"}
				onClick={() => setLocale(locale === "ar" ? "en" : "ar")}
			>
				{t("driver.home.otherLanguage")}
			</Button>
		</Card>
	);
}

/** Where the app remembers that it started a sign-in on its own. */
export const AUTO_SIGN_IN_KEY = "khazzin.driver.autoSignIn";

/** Whether this address asks for the sign-in to start without a tap. */
export function autoSignIn(at: { pathname: string; search: string }): boolean {
	return at.pathname === "/callback" || new URLSearchParams(at.search).has(SIGN_IN_HINT);
}

/**
 * Another driver's work is on this phone and has not reached the server
 * (ADJ-M03-4). Signing out keeps it, so its owner can sign in and sync it;
 * discarding it is a choice the driver makes knowingly, never a side effect
 * of signing in.
 */
export function HeldWork({ work }: { work: PendingWork }) {
	const { t } = useI18n();
	const { signOut } = useAuth();
	const api = useApi();
	const { discardHeld } = useOffline();
	const [busy, setBusy] = useState<"leave" | "discard" | null>(null);

	return (
		<Card>
			<div className="flex flex-col gap-1">
				<h1 className="text-[15px] font-bold tracking-tight text-fg">
					{t("driver.home.heldTitle")}
				</h1>
				<p className="text-[13px] leading-relaxed text-fg-muted">
					{t("driver.home.heldBody", { commands: work.commands, files: work.files })}
				</p>
			</div>
			<Button
				variant="primary"
				size="lg"
				loading={busy === "leave"}
				disabled={busy !== null}
				onClick={() => {
					setBusy("leave");
					void endSession(api, signOut).finally(() => setBusy(null));
				}}
			>
				{t("driver.home.heldSignOut")}
			</Button>
			<Button
				variant="danger"
				size="lg"
				loading={busy === "discard"}
				disabled={busy !== null}
				onClick={() => {
					setBusy("discard");
					void discardHeld().finally(() => setBusy(null));
				}}
			>
				{t("driver.home.heldDiscard")}
			</Button>
		</Card>
	);
}

function Card({ children }: { children: ReactNode }) {
	return (
		<main className="grid min-h-screen place-items-center bg-bg-canvas px-5 py-10">
			<div className="flex w-full max-w-[400px] flex-col gap-6 card p-8 text-center">
				<Forklift size={96} className="mx-auto text-brand-ink" />
				{children}
			</div>
		</main>
	);
}
